import React, { useState } from 'react';
import { Button, Modal, Form, Input, Radio, message } from 'antd';
import { CommentOutlined, BugOutlined, BulbOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../lib/config';

const PLAYER_ID_KEY = 'tth_player_id';

type FeedbackType = 'bug' | 'feature' | 'general';

interface FeedbackForm {
  type: FeedbackType;
  message: string;
  contactEmail?: string;
}

interface FeedbackButtonProps {
  variant?: 'floating' | 'inline';
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ variant = 'floating' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<FeedbackForm>();

  const handleOpen = () => {
    form.resetFields();
    form.setFieldsValue({ type: 'general' });
    setOpen(true);
  };

  const handleSubmit = async (values: FeedbackForm) => {
    setSubmitting(true);
    const playerId = localStorage.getItem(PLAYER_ID_KEY);

    try {
      const res = await fetch(`${getApiUrl()}/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(playerId ? { 'X-Player-ID': playerId } : {}),
        },
        body: JSON.stringify({
          type: values.type,
          message: values.message,
          pageUrl: window.location.pathname,
          playerId: playerId ?? undefined,
          contactEmail: values.contactEmail?.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(err.message ?? 'Failed to submit feedback');
      }

      message.success(t('feedback.successMessage'));
      setOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error';
      if (msg.includes('Too many')) {
        message.error(t('feedback.rateLimitError'));
      } else {
        message.error(t('feedback.errorMessage'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {variant === 'floating' ? (
        <Button
          type="primary"
          shape="circle"
          icon={<CommentOutlined />}
          size="large"
          onClick={handleOpen}
          title={t('feedback.buttonTitle')}
          aria-label={t('feedback.buttonTitle')}
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 1000,
            width: 52,
            height: 52,
            boxShadow: '0 4px 16px rgba(24,144,255,0.45)',
          }}
        />
      ) : (
        <Button
          icon={<CommentOutlined />}
          onClick={handleOpen}
          style={{ width: '100%' }}
        >
          {t('feedback.buttonTitle')}
        </Button>
      )}

      <Modal
        open={open}
        title={t('feedback.modalTitle')}
        onCancel={() => setOpen(false)}
        footer={null}
        width={440}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ type: 'general' }}
        >
          <Form.Item name="type" label={t('feedback.typeLabel')}>
            <Radio.Group>
              <Radio.Button value="bug">
                <BugOutlined style={{ marginRight: 6 }} />
                {t('feedback.typeBug')}
              </Radio.Button>
              <Radio.Button value="feature">
                <BulbOutlined style={{ marginRight: 6 }} />
                {t('feedback.typeFeature')}
              </Radio.Button>
              <Radio.Button value="general">
                <MessageOutlined style={{ marginRight: 6 }} />
                {t('feedback.typeGeneral')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="message"
            label={t('feedback.messageLabel')}
            rules={[
              { required: true, message: t('feedback.messageRequired') },
              { min: 3, message: t('feedback.messageRequired') },
            ]}
          >
            <Input.TextArea
              rows={4}
              maxLength={2000}
              showCount
              placeholder={t('feedback.messagePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="contactEmail"
            label={t('feedback.contactEmailLabel')}
            extra={t('feedback.contactEmailHelp')}
            rules={[{ type: 'email', message: t('feedback.contactEmailInvalid') }]}
          >
            <Input
              type="email"
              maxLength={200}
              placeholder={t('feedback.contactEmailPlaceholder')}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => setOpen(false)} style={{ marginRight: 8 }}>
              {t('feedback.cancelButton')}
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('feedback.submitButton')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FeedbackButton;
